import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { db } from '../../firebase';
import {
  collection, query, orderBy, onSnapshot, doc,
  addDoc, updateDoc, deleteDoc, arrayUnion, arrayRemove, serverTimestamp, increment
} from 'firebase/firestore';
import { addPostToDb, updateNewPostText, setPosts, deletePostFromDb } from '../../redux/profileSlice';

const REACTIONS = [
  { key: 'accepted', emoji: '✅', label: 'Принято' },
  { key: 'done', emoji: '🎯', label: 'Выполнено' },
  { key: 'postponed', emoji: '⏳', label: 'Отложено' },
];

/* ========== ПОМОЩНИКИ ========== */
const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const formatDateRu = (s) => (s ? s.split('-').reverse().join('.') : '');
const formatDateTime = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const getHighlight = (post, now) => {
  if (!post.eventDate) return null;
  if (post.eventDate !== toLocalDateStr(now)) return null;
  if (post.eventTime) {
    const [h, m] = post.eventTime.split(':').map(Number);
    const event = new Date(now);
    event.setHours(h, m, 0, 0);
    const diff = now.getTime() - event.getTime();
    if (diff >= -3600000 && diff <= 3600000) return 'red';
  }
  return 'green';
};
const snippet = (text, len) => {
  const t = text || '';
  return t.length > len ? t.slice(0, len) + '…' : t;
};
const useSmartInput = (text, setText) => {
  const handleChange = (e) => {
    let v = e.target.value;
    if (v.startsWith('... ')) v = 'Уважаемые коллеги! ' + v.slice(4);
    else if (v.startsWith('. ')) v = 'Добрый день! ' + v.slice(2);
    setText(v);
  };
  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert('Голосовой ввод не поддерживается этим браузером. Откройте сайт в Chrome (Android) или Edge.');
      return;
    }
    const startRecognition = () => {
      const rec = new SR();
      rec.lang = 'ru-RU';
      rec.continuous = false;
      rec.interimResults = false;
      rec.onresult = (e) => {
        const t = e.results[0][0].transcript;
        setText((prev) => prev ? prev + ' ' + t : t);
      };
      rec.onerror = (e) => {
        if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
          alert('Нет доступа к микрофону. Нажмите на значок 🔒 слева от адреса сайта и разрешите микрофон.');
        } else {
          alert('Не удалось распознать речь (' + e.error + '). Попробуйте ещё раз.');
        }
      };
      rec.start();
    };
    // Явно запрашиваем разрешение на микрофон (важно для Android)
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then((stream) => { stream.getTracks().forEach((t) => t.stop()); startRecognition(); })
        .catch(() => alert('Нет доступа к микрофону. Разрешите доступ в настройках браузера (🔒 слева от адреса сайта).'));
    } else {
      startRecognition();
    }
  };
  return { handleChange, startVoice };
};

/* ========== ПОСТ ========== */
const PostItem = ({ post, user, topicName, usersById, groupsById, usersList, groupsList, now, onReply, onJump, flash, deletedById }) => {
  const dispatch = useDispatch();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editTaggedUsers, setEditTaggedUsers] = useState([]);
  const [editTaggedGroups, setEditTaggedGroups] = useState([]);
  const [showEditTagPicker, setShowEditTagPicker] = useState(false);

  const isMyPost = post.authorId === user?.uid;
  const canPost = !!user?.role && user.role !== 'user' && user.role !== 'newbie';
  const readOnly = !!user?.blocked;

  const highlight = getHighlight(post, now);
  const textStyle =
    highlight === 'green'
      ? { color: '#2e7d32', background: 'rgba(76,175,80,0.15)', padding: '4px 8px', borderRadius: '6px' }
      : highlight === 'red'
      ? { color: '#c62828', background: 'rgba(244,67,54,0.18)', padding: '4px 8px', borderRadius: '6px', fontWeight: 700 }
      : {};

  useEffect(() => {
    const q = query(collection(db, 'posts', post.id, 'comments'), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setComments(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => console.error('Ошибка загрузки комментариев:', error));
    return () => unsubscribe();
  }, [post.id]);

  const handleReact = async (key) => {
    try {
      const ref = doc(db, 'posts', post.id);
      const reactions = post.reactions || {};
      const current = Object.keys(reactions).find((k) => (reactions[k] || []).includes(user.uid));
      const updates = {};
      if (current === key) {
        updates[`reactions.${key}`] = arrayRemove(user.uid);
      } else {
        updates[`reactions.${key}`] = arrayUnion(user.uid);
        if (current) updates[`reactions.${current}`] = arrayRemove(user.uid);
      }
      await updateDoc(ref, updates);
    } catch (error) {
      console.error('Ошибка реакции:', error);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    try {
      await addDoc(collection(db, 'posts', post.id, 'comments'), {
        text: commentText,
        authorEmail: user.email,
        authorName: user.displayName || user.email,
        createdAt: serverTimestamp(),
      });
      setCommentText('');
    } catch (error) {
      console.error('Ошибка комментария:', error);
    }
  };

  const startEdit = () => {
    setEditText(post.text);
    setEditDate(post.eventDate || '');
    setEditTime(post.eventTime || '');
    setEditTaggedUsers(post.taggedUsers || []);
    setEditTaggedGroups(post.taggedGroups || []);
    setIsEditing(true);
  };

  const saveEdit = async () => {
    if (!editText.trim()) return;
    try {
      await updateDoc(doc(db, 'posts', post.id), {
        text: editText.trim(),
        edited: true,
        eventDate: editDate || null,
        eventTime: editDate && editTime ? editTime : null,
        taggedUsers: editTaggedUsers,
        taggedGroups: editTaggedGroups,
      });

      const authorName = user.displayName || user.email;
      const dateSuffix =
        (editDate ? ` 📅 ${formatDateRu(editDate)}` : '') +
        (editDate && editTime ? ` ⏰ ${editTime}` : '');

      // Старым отмеченным — об изменении
      for (const uid of (post.taggedUsers || [])) {
        if (uid === user.uid) continue;
        const chatId = [user.uid, uid].sort().join('_');
        await addDoc(collection(db, 'chats', chatId, 'messages'), {
          type: 'text',
          text: `✏️ ${authorName} изменил(а) пост, в котором вы отмечены: «${editText.trim()}»${dateSuffix}`,
          from: user.uid, fromEmail: user.email,
          to: uid, read: false, createdAt: serverTimestamp(),
        });
        await updateDoc(doc(db, 'users', uid), { [`unreadFrom.${user.uid}`]: increment(1) });
      }

      // НОВЫМ отмеченным — об отметке
      const newUsers = editTaggedUsers.filter((id) => !(post.taggedUsers || []).includes(id));
      for (const uid of newUsers) {
        if (uid === user.uid) continue;
        const chatId = [user.uid, uid].sort().join('_');
        await addDoc(collection(db, 'chats', chatId, 'messages'), {
          type: 'text',
          text: `🔖 ${authorName} отметил(а) вас в посте: «${editText.trim()}»${dateSuffix}`,
          from: user.uid, fromEmail: user.email,
          to: uid, read: false, createdAt: serverTimestamp(),
        });
        await updateDoc(doc(db, 'users', uid), { [`unreadFrom.${user.uid}`]: increment(1) });
      }

      // Старым группам — об изменении
      for (const gid of (post.taggedGroups || [])) {
        await addDoc(collection(db, 'groups', gid, 'posts'), {
          text: `✏️ ${authorName} изменил(а) пост, отмеченный в группе: «${editText.trim()}»${dateSuffix}`,
          authorId: user.uid, authorEmail: user.email, authorName,
          groupId: gid, fromFeed: true, createdAt: serverTimestamp(),
        });
      }
      // НОВЫМ группам — дубликат
      const newGroups = editTaggedGroups.filter((id) => !(post.taggedGroups || []).includes(id));
      for (const gid of newGroups) {
        await addDoc(collection(db, 'groups', gid, 'posts'), {
          text: `📢 ${authorName} (из ленты): «${editText.trim()}»${dateSuffix}`,
          authorId: user.uid, authorEmail: user.email, authorName,
          groupId: gid, fromFeed: true, createdAt: serverTimestamp(),
        });
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Ошибка редактирования:', error);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditText('');
    setEditDate('');
    setEditTime('');
    setShowEditTagPicker(false);
  };

  const smallBtn = {
    background: 'transparent', border: 'none', cursor: 'pointer',
    fontSize: '14px', margin: '0 2px', verticalAlign: 'middle', opacity: 0.8,
  };
  const chipTiny = (bg, color) => ({
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    background: bg, color, padding: '2px 8px', borderRadius: '12px',
    fontSize: '11px', fontWeight: 600,
  });

  return (
    <div
      id={'post-' + post.id}
      style={{
        background: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.5)',
        padding: '12px', borderRadius: '10px', marginBottom: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        outline: flash ? '3px solid #2a5298' : 'none',
        transition: 'outline 0.3s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
          <strong style={{ color: '#1e3c72', fontSize: '14px' }}>
            {deletedById && deletedById[post.authorId]
              ? '[удалённый пользователь]'
              : (post.authorName || (post.authorEmail ? post.authorEmail.split('@')[0] : 'Аноним'))}
          </strong>
          <span style={{ fontSize: '11px', color: '#999' }}>{formatDateTime(post.createdAt)}</span>
          {topicName && (
            <span style={{ background: '#e3f2fd', color: '#1e3c72', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 }}>
              # {topicName}
            </span>
          )}
          {(post.eventDate || post.eventTime) && (
            <span style={{
              background: highlight === 'red' ? '#ffebee' : highlight === 'green' ? '#e8f5e9' : '#f0f2f5',
              color: highlight === 'red' ? '#c62828' : highlight === 'green' ? '#2e7d32' : '#666',
              padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
            }}>
              📅 {formatDateRu(post.eventDate)}{post.eventTime ? ` ⏰ ${post.eventTime}` : ''}
            </span>
          )}
        </div>
        {(isMyPost || user?.role === 'admin' || user?.role === 'moderator') && !isEditing && (
          <div>
            {isMyPost && <button onClick={startEdit} title="Редактировать" style={smallBtn}>✏️</button>}
            <button
              onClick={() => { if (window.confirm('Удалить этот пост?')) dispatch(deletePostFromDb(post.id)); }}
              title="Удалить"
              style={{ ...smallBtn, color: '#ff4d4f' }}
            >🗑️</button>
          </div>
        )}
      </div>

      {post.replyTo && (
        <div
          onClick={() => onJump && onJump(post.replyTo.postId)}
          title="Перейти к исходному посту"
          style={{
            borderLeft: '3px solid #2a5298', background: 'rgba(42,82,152,0.08)',
            padding: '6px 10px', borderRadius: '6px', marginBottom: '8px',
            fontSize: '12px', color: '#2a5298', cursor: 'pointer',
          }}
        >
          ↩ Ответ на пост {post.replyTo.authorName || 'автора'}: «{snippet(post.replyTo.text, 80)}»
        </div>
      )}

      {(post.taggedUsers || []).length > 0 && (
        <div style={{ fontSize: '12px', color: '#2a5298', marginBottom: '4px', fontWeight: 600 }}>
          👤 Отмечены: {post.taggedUsers.map((id) => usersById[id] || '—').join(', ')}
        </div>
      )}
      {(post.taggedGroups || []).length > 0 && (
        <div style={{ fontSize: '12px', color: '#2a5298', marginBottom: '6px', fontWeight: 600 }}>
          🏘 В группы: {post.taggedGroups.map((id) => groupsById[id] || '—').join(', ')}
        </div>
      )}

      {isEditing ? (
        <div style={{ marginBottom: '8px' }}>
          <textarea value={editText} onChange={(e) => setEditText(e.target.value)}
            style={{ width: '100%', height: '70px', padding: '8px', border: '1px solid #2a5298', borderRadius: '5px', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '14px' }} />

          <div style={{ display: 'flex', gap: '8px', marginTop: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', color: '#888' }}>📅 Дата:</span>
            <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)}
              style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '12px' }} />
            <span style={{ fontSize: '12px', color: '#888' }}>⏰ Время:</span>
            <input type="time" value={editTime} onChange={(e) => setEditTime(e.target.value)} disabled={!editDate}
              style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '12px', opacity: editDate ? 1 : 0.5 }} />
            {(editDate || editTime) && (
              <button onClick={() => { setEditDate(''); setEditTime(''); }} title="Убрать дату и время"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4f', fontWeight: 700 }}>✕</button>
            )}
          </div>

          {/* Изменение отметок */}
          <div style={{ display: 'flex', gap: '6px', marginTop: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => setShowEditTagPicker((v) => !v)}
              style={{ padding: '3px 8px', background: '#f0f2f5', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}>
              👤 Отметить
            </button>
            {editTaggedUsers.map((id) => (
              <span key={id} style={chipTiny('#e3f2fd', '#1e3c72')}>
                👤 {usersById[id] || '—'}
                <button onClick={() => setEditTaggedUsers((p) => p.filter((t) => t !== id))} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4f', fontWeight: 700, fontSize: '11px' }}>✕</button>
              </span>
            ))}
            {editTaggedGroups.map((id) => (
              <span key={id} style={chipTiny('#e8f5e9', '#2e7d32')}>
                🏘 {groupsById[id] || '—'}
                <button onClick={() => setEditTaggedGroups((p) => p.filter((t) => t !== id))} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4f', fontWeight: 700, fontSize: '11px' }}>✕</button>
              </span>
            ))}
          </div>
          {showEditTagPicker && (
            <div style={{ border: '1px solid #ddd', borderRadius: '8px', maxHeight: '140px', overflowY: 'auto', background: '#fff', padding: '6px', marginTop: '6px' }}>
              <p style={{ margin: '2px 0', fontSize: '11px', color: '#888', fontWeight: 700 }}>Пользователи:</p>
              {usersList.filter((u) => u.id !== user?.uid).map((u) => (
                <label key={u.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '3px 6px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={editTaggedUsers.includes(u.id)}
                    onChange={() => setEditTaggedUsers((p) => p.includes(u.id) ? p.filter((t) => t !== u.id) : [...p, u.id])} />
                  <span style={{ fontSize: '12px' }}>{u.displayName || (u.email ? u.email.split('@')[0] : 'Пользователь')}</span>
                </label>
              ))}
              <p style={{ margin: '4px 0 2px 0', fontSize: '11px', color: '#888', fontWeight: 700 }}>Группы:</p>
              {groupsList.map((g) => (
                <label key={g.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '3px 6px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={editTaggedGroups.includes(g.id)}
                    onChange={() => setEditTaggedGroups((p) => p.includes(g.id) ? p.filter((t) => t !== g.id) : [...p, g.id])} />
                  <span style={{ fontSize: '12px' }}>🏘 {g.name}</span>
                </label>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
            <button onClick={saveEdit} style={{ padding: '5px 12px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px' }}>✅ Сохранить</button>
            <button onClick={cancelEdit} style={{ padding: '5px 12px', background: '#ccc', color: '#333', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px' }}>Отмена</button>
          </div>
        </div>
      ) : (
        <p style={{ margin: '0 0 8px 0', fontSize: '14px', whiteSpace: 'pre-wrap', lineHeight: 1.4, ...textStyle }}>
          {post.text}
          {post.edited && <em style={{ fontSize: '11px', color: '#999' }}> (изменено)</em>}
        </p>
      )}

      {post.fileData && (
        <a href={post.fileData} download={post.fileName}
          style={{ display: 'inline-block', marginBottom: '8px', padding: '6px 12px', background: '#f0f2f5', borderRadius: '8px', color: '#2a5298', fontWeight: 600, textDecoration: 'none', fontSize: '13px' }}>
          📥 {post.fileName}
        </a>
      )}

      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', borderTop: '1px solid #eee', paddingTop: '8px', flexWrap: 'wrap' }}>
        {!readOnly && REACTIONS.map((r) => {
          const uids = (post.reactions || {})[r.key] || [];
          const active = uids.includes(user?.uid);
          return (
            <button key={r.key} onClick={() => handleReact(r.key)}
              title={r.label + (uids.length ? ': ' + uids.map((id) => usersById[id] || '?').join(', ') : '')}
              style={{
                background: active ? '#e3f2fd' : '#f5f5f5',
                border: active ? '1px solid #2a5298' : '1px solid transparent',
                borderRadius: '12px', padding: '2px 8px', cursor: 'pointer',
                fontSize: '12px', fontWeight: active ? 700 : 400,
                color: active ? '#1e3c72' : '#666',
              }}>
              {r.emoji}{uids.length > 0 ? ` ${uids.length}` : ''}
            </button>
          );
        })}
        {canPost && !readOnly && (
          <button onClick={() => onReply && onReply(post)} title="Ответить на пост"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', color: '#888', marginLeft: 'auto' }}>
            ↩ Ответить
          </button>
        )}
        <button onClick={() => setShowComments(!showComments)} title="Комментарии"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', color: '#888', marginLeft: canPost && !readOnly ? '0' : 'auto' }}>
          💬 {comments.length}
        </button>
      </div>

      {!readOnly && REACTIONS.some((r) => ((post.reactions || {})[r.key] || []).length > 0) && (
        <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', lineHeight: 1.5 }}>
          {REACTIONS.map((r) => {
            const uids = (post.reactions || {})[r.key] || [];
            if (uids.length === 0) return null;
            return (
              <span key={r.key} style={{ marginRight: '10px' }}>
                {r.emoji} {uids.map((id) => usersById[id] || '—').join(', ')}
              </span>
            );
          })}
        </div>
      )}

      {showComments && (
        <div style={{ marginTop: '8px', background: 'rgba(249,249,249,0.6)', borderRadius: '8px', padding: '8px' }}>
          {comments.length === 0 ? (
            <p style={{ color: '#888', fontSize: '12px', margin: '0 0 8px 0' }}>Комментариев пока нет.</p>
          ) : (
            comments.map((c) => (
              <div key={c.id} style={{ marginBottom: '6px', fontSize: '13px' }}>
                <strong style={{ color: '#1e3c72' }}>
                  {deletedById && deletedById[c.authorId] ? '[удалённый пользователь]' : (c.authorName || (c.authorEmail ? c.authorEmail.split('@')[0] : 'Аноним'))}:
                </strong>{' '}
                {c.text}
              </div>
            ))
          )}
          {!readOnly && (
          <div style={{ display: 'flex', gap: '6px' }}>
            <input type="text" value={commentText} onChange={(e) => setCommentText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
              placeholder="Комментарий..."
              style={{ flex: 1, padding: '6px 8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px' }} />
            <button onClick={handleAddComment} style={{ padding: '6px 12px', background: '#2a5298', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px' }}>→</button>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ========== ЛЕНТА ========== */
const Feed = () => {
  const dispatch = useDispatch();
  const { posts, newPostText } = useSelector((state) => state.profile);
  const { user } = useSelector((state) => state.auth);

  const [topics, setTopics] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [usersById, setUsersById] = useState({});
  const [groupsList, setGroupsList] = useState([]);
  const [groupsById, setGroupsById] = useState({});
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);
  const [onlyMyPosts, setOnlyMyPosts] = useState(false);
  const [showTopicForm, setShowTopicForm] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [editTopicName, setEditTopicName] = useState('');
  const [searchText, setSearchText] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [taggedGroups, setTaggedGroups] = useState([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [flashPostId, setFlashPostId] = useState(null);
  const [now, setNow] = useState(new Date());
  const fileInputRef = useRef(null);
  const formRef = useRef(null);
  const textareaRef = useRef(null);
  const { handleChange: handleTextChange, startVoice } = useSmartInput(newPostText, (v) => dispatch(updateNewPostText(v)));

  const isAdmin = user?.role === 'admin' || user?.role === 'moderator';
  const canPost = !!user?.role && user.role !== 'user' && user.role !== 'newbie' && !user?.blocked;
  const isBlocked = !!user?.blocked;
  const deletedById = {};
  usersList.forEach((u) => { deletedById[u.id] = !!u.deleted; });

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      dispatch(setPosts(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))));
    });
    return () => unsubscribe();
  }, [dispatch]);

  useEffect(() => {
    const q = query(collection(db, 'topics'), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setTopics(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => console.error('Ошибка загрузки тем:', error));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsersList(list);
      const map = {};
      list.forEach((u) => {
        map[u.id] = u.displayName || (u.email ? u.email.split('@')[0] : 'Пользователь');
      });
      setUsersById(map);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'groups'), (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setGroupsList(list);
      const map = {};
      list.forEach((g) => { map[g.id] = g.name; });
      setGroupsById(map);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const mark = () => localStorage.setItem('lastFeedSeen', String(Date.now()));
    mark();
    const interval = setInterval(mark, 30000);
    return () => clearInterval(interval);
  }, []);

  const toggleTopic = (id) => {
    setSelectedTopicIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };
  const toggleTag = (id) => {
    setTaggedUsers((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };
  const toggleGroupTag = (id) => {
    setTaggedGroups((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const jumpToPost = (postId) => {
    setSelectedTopicIds([]);
    setSearchText('');
    setOnlyMyPosts(false);
    setTimeout(() => {
      const el = document.getElementById('post-' + postId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setFlashPostId(postId);
        setTimeout(() => setFlashPostId(null), 2000);
      }
    }, 200);
  };

  const startEditTopic = (topic) => { setEditingTopicId(topic.id); setEditTopicName(topic.name); };
  const saveEditTopic = async (topicId) => {
    if (!editTopicName.trim()) return;
    try {
      await updateDoc(doc(db, 'topics', topicId), { name: editTopicName.trim() });
      setEditingTopicId(null); setEditTopicName('');
    } catch (error) { console.error('Ошибка переименования темы:', error); }
  };
  const deleteTopic = async (topicId) => {
    if (!window.confirm('Удалить тему? Посты этой темы останутся в ленте и будут видны в «📰 Все».')) return;
    try {
      await deleteDoc(doc(db, 'topics', topicId));
      setSelectedTopicIds((prev) => prev.filter((id) => id !== topicId));
    } catch (error) { console.error('Ошибка удаления темы:', error); }
  };

  const handleFilePick = (file) => {
    if (file.size > 900 * 1024) { alert('Файл слишком большой! Лимит — 900 КБ.'); return; }
    const reader = new FileReader();
    reader.onloadend = () => {
      setPendingFile({ fileName: file.name, fileType: file.type, fileData: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const postTopicId = selectedTopicIds.length > 0 ? selectedTopicIds[0] : null;

  const handleAddPost = () => {
    if (newPostText.trim() || pendingFile) {
      dispatch(addPostToDb({
        text: newPostText,
        topicId: postTopicId,
        file: pendingFile,
        eventDate: eventDate || null,
        eventTime: eventDate && eventTime ? eventTime : null,
        taggedUsers: taggedUsers,
        taggedGroups: taggedGroups,
        replyTo: replyTo,
      }));
      setPendingFile(null);
      setEventDate('');
      setEventTime('');
      setTaggedUsers([]);
      setTaggedGroups([]);
      setShowTagPicker(false);
      setReplyTo(null);
    }
  };

  const handleCreateTopic = async () => {
    if (!newTopicName.trim()) return;
    try {
      await addDoc(collection(db, 'topics'), { name: newTopicName.trim(), createdAt: serverTimestamp() });
      setNewTopicName(''); setShowTopicForm(false);
    } catch (error) { console.error('Ошибка создания темы:', error); }
  };

  const topicNameById = (id) => {
    const t = topics.find((t) => t.id === id);
    return t ? t.name : null;
  };

  const visiblePosts = posts
    .filter((p) => selectedTopicIds.length === 0 || selectedTopicIds.includes(p.topicId))
    .filter((p) => !onlyMyPosts || p.authorId === user?.uid)
    .filter((p) => {
      const q = searchText.trim().toLowerCase();
      if (!q) return true;
      return (
        (p.text || '').toLowerCase().includes(q) ||
        (p.authorName || '').toLowerCase().includes(q) ||
        (p.authorEmail || '').toLowerCase().includes(q) ||
        (p.fileName || '').toLowerCase().includes(q)
      );
    });

  const chipStyle = (active, isPlus) => ({
    flex: '0 0 auto', padding: '6px 12px', borderRadius: '16px', border: 'none',
    cursor: 'pointer', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap',
    background: isPlus ? '#4caf50' : active ? '#2a5298' : '#fff',
    color: active || isPlus ? '#fff' : '#333',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  });
  const tinyBtn = {
    background: 'transparent', border: 'none', cursor: 'pointer',
    fontSize: '11px', padding: '0 2px', lineHeight: 1, opacity: 0.85,
  };

  return (
    <div style={{ background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '12px', padding: '10px' }}>
      <h3 style={{ margin: '0 0 10px 0', color: '#1e3c72' }}>📰 Лента</h3>

      {isBlocked && (
        <div style={{ background: '#ffebee', color: '#c62828', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>
          🚫 Вы заблокированы администратором — доступен только просмотр
        </div>
      )}

      <input
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder="🔍 Поиск по ленте..."
        style={{ width: '100%', padding: '8px 12px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '16px', boxSizing: 'border-box', fontSize: '13px', background: '#fff' }}
      />

      <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', padding: '3px 0 8px 0' }}>
        <button onClick={() => { setSelectedTopicIds([]); setOnlyMyPosts(false); }} style={chipStyle(selectedTopicIds.length === 0 && !onlyMyPosts, false)}>📰 Все</button>
        <button onClick={() => setOnlyMyPosts((v) => !v)} style={chipStyle(onlyMyPosts, false)}>👤 Мои посты</button>
        {topics.map((t) => {
          const isSelected = selectedTopicIds.includes(t.id);
          if (isAdmin && editingTopicId === t.id) {
            return (
              <span key={t.id} style={{ display: 'inline-flex', gap: '4px', alignItems: 'center', flex: '0 0 auto' }}>
                <input value={editTopicName} onChange={(e) => setEditTopicName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && saveEditTopic(t.id)} autoFocus
                  style={{ width: '110px', padding: '5px 8px', borderRadius: '12px', border: '1px solid #2a5298', fontSize: '13px' }} />
                <button onClick={() => saveEditTopic(t.id)} title="Сохранить" style={{ ...tinyBtn, fontSize: '14px' }}>✅</button>
                <button onClick={() => setEditingTopicId(null)} title="Отмена" style={{ ...tinyBtn, fontSize: '14px' }}>✕</button>
              </span>
            );
          }
          return (
            <span key={t.id} onClick={() => toggleTopic(t.id)} title="Можно выбрать несколько тем"
              style={{ ...chipStyle(isSelected, false), display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              # {t.name}
              {isAdmin && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); startEditTopic(t); }} title="Переименовать тему" style={tinyBtn}>✏️</button>
                  <button onClick={(e) => { e.stopPropagation(); deleteTopic(t.id); }} title="Удалить тему" style={tinyBtn}>🗑️</button>
                </>
              )}
            </span>
          );
        })}
        {isAdmin && (
          <button onClick={() => setShowTopicForm(!showTopicForm)} style={chipStyle(false, true)} title="Создать тему">＋</button>
        )}
      </div>

      {selectedTopicIds.length > 1 && (
        <p style={{ margin: '0 0 8px 0', fontSize: '11px', color: '#888' }}>Выбрано тем: {selectedTopicIds.length}</p>
      )}

      {isAdmin && showTopicForm && (
        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
          <input value={newTopicName} onChange={(e) => setNewTopicName(e.target.value)} placeholder="Название темы"
            style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '13px' }}
            onKeyPress={(e) => e.key === 'Enter' && handleCreateTopic()} />
          <button onClick={handleCreateTopic} style={{ padding: '8px 12px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>Создать</button>
        </div>
      )}

      {canPost && (
      <div ref={formRef} style={{ background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.5)', padding: '10px', borderRadius: '10px', marginBottom: '12px' }}>
        {replyTo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', padding: '6px 10px', background: '#e3f2fd', borderRadius: '8px', fontSize: '12px', color: '#1e3c72' }}>
            <span>↩ Ответ на пост {replyTo.authorName}: «{snippet(replyTo.text, 60)}»</span>
            <button onClick={() => setReplyTo(null)} title="Отменить ответ"
              style={{ marginLeft: 'auto', background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4f', fontWeight: 700 }}>✕</button>
          </div>
        )}

        <textarea ref={textareaRef} value={newPostText} onChange={handleTextChange}
          placeholder={replyTo ? 'Ваш ответ...' : 'Что у вас нового?'}
          style={{ width: '100%', height: '60px', padding: '8px', border: '1px solid #ddd', borderRadius: '5px', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '13px' }} />

        {pendingFile && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '6px', padding: '4px 10px', background: '#f0f2f5', borderRadius: '12px', fontSize: '12px' }}>
            📎 {pendingFile.fileName}
            <button onClick={() => setPendingFile(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4f', fontWeight: 700 }}>✕</button>
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', color: '#888' }}>📅 Дата:</span>
          <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)}
            style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '12px' }} />
          <span style={{ fontSize: '12px', color: '#888' }}>⏰ Время:</span>
          <input type="time" value={eventTime} onChange={(e) => setEventTime(e.target.value)} disabled={!eventDate}
            style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '12px', opacity: eventDate ? 1 : 0.5 }} />
          {(eventDate || eventTime) && (
            <button onClick={() => { setEventDate(''); setEventTime(''); }} title="Сбросить дату и время"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4f', fontWeight: 700 }}>✕</button>
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => setShowTagPicker(!showTagPicker)}
            style={{ padding: '4px 10px', background: showTagPicker ? '#2a5298' : '#f0f2f5', color: showTagPicker ? '#fff' : '#333', border: 'none', borderRadius: '14px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
            👤 Отметить
          </button>
          {taggedUsers.map((id) => (
            <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#e3f2fd', color: '#1e3c72', padding: '3px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
              👤 {usersById[id] || '—'}
              <button onClick={() => toggleTag(id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4f', fontWeight: 700, fontSize: '12px' }}>✕</button>
            </span>
          ))}
          {taggedGroups.map((id) => (
            <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#e8f5e9', color: '#2e7d32', padding: '3px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
              🏘 {groupsById[id] || '—'}
              <button onClick={() => toggleGroupTag(id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4f', fontWeight: 700, fontSize: '12px' }}>✕</button>
            </span>
          ))}
        </div>
        {showTagPicker && (
          <div style={{ border: '1px solid #ddd', borderRadius: '8px', maxHeight: '180px', overflowY: 'auto', background: '#fff', padding: '6px', marginTop: '6px' }}>
            <p style={{ margin: '2px 0', fontSize: '11px', color: '#888', fontWeight: 700 }}>Пользователи:</p>
            {usersList.filter((u) => u.id !== user?.uid).map((u) => (
              <label key={u.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '4px 6px', cursor: 'pointer' }}>
                <input type="checkbox" checked={taggedUsers.includes(u.id)} onChange={() => toggleTag(u.id)} />
                <span style={{ fontSize: '13px' }}>{u.displayName || (u.email ? u.email.split('@')[0] : 'Пользователь')}</span>
              </label>
            ))}
            <p style={{ margin: '6px 0 2px 0', fontSize: '11px', color: '#888', fontWeight: 700 }}>Группы:</p>
            {groupsList.length === 0 ? (
              <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>Групп пока нет.</p>
            ) : (
              groupsList.map((g) => (
                <label key={g.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '4px 6px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={taggedGroups.includes(g.id)} onChange={() => toggleGroupTag(g.id)} />
                  <span style={{ fontSize: '13px' }}>🏘 {g.name}</span>
                </label>
              ))
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
          <input type="file" ref={fileInputRef} style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files[0]; if (f) handleFilePick(f); e.target.value = ''; }} />
          <button onClick={() => fileInputRef.current && fileInputRef.current.click()} title="Прикрепить файл (до 900 КБ)"
            style={{ padding: '8px 12px', background: '#f0f2f5', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '14px' }}>📎</button>
          {window.innerWidth > 768 && (
            <button onClick={startVoice} title="Ввести голосом"
              style={{ padding: '8px 12px', background: '#f0f2f5', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '14px' }}>🎤</button>
          )}
          <button onClick={handleAddPost} disabled={!newPostText.trim() && !pendingFile}
            style={{ padding: '8px 16px', background: !newPostText.trim() && !pendingFile ? '#ccc' : '#2a5298', color: '#fff', border: 'none', borderRadius: '5px', cursor: !newPostText.trim() && !pendingFile ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: '13px' }}>
            {replyTo ? '↩ Ответить' : 'Опубликовать'}
          </button>
        </div>
        <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#888' }}>
          Тема публикации: {postTopicId ? '# ' + topicNameById(postTopicId) : '📰 общая лента (без темы)'}
          {selectedTopicIds.length > 1 && ' (первая из выбранных)'}
        </p>
      </div>
      )}

      {searchText.trim() && (
        <p style={{ fontSize: '12px', color: '#888', margin: '0 0 8px 0' }}>Найдено: {visiblePosts.length}</p>
      )}
      {visiblePosts.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#888', fontSize: '13px' }}>
          {searchText.trim() ? 'Ничего не найдено.' : onlyMyPosts ? 'У вас пока нет постов.' : 'Постов пока нет. Будьте первым!'}
        </p>
      ) : (
        visiblePosts.map((post) => (
          <PostItem
            key={post.id}
            post={post}
            user={user}
            topicName={topicNameById(post.topicId)}
            usersById={usersById}
            groupsById={groupsById}
            usersList={usersList}
            groupsList={groupsList}
            deletedById={deletedById}
            now={now}
            onReply={(p) => {
              setReplyTo({ postId: p.id, text: p.text, authorName: p.authorName });
              setTimeout(() => {
                if (formRef.current) formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                if (textareaRef.current) textareaRef.current.focus();
              }, 100);
            }}
            onJump={jumpToPost}
            flash={flashPostId === post.id}
          />
        ))
      )}
    </div>
  );
};

export default Feed;